import React, { useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { ContactForm } from '../types';
import '../styles/ContactSection.css';

const WHATSAPP_NUMBER = '5553997102819';
const MESSAGE_MAX = 300;

const buildWhatsAppUrl = (data: ContactForm): string => {
  const parts = [
    `Olá! Me chamo *${data.name}*.`,
    data.subject ? `Assunto: *${data.subject}*` : '',
    data.message ? `\n${data.message}` : '',
    data.phone ? `\nMeu WhatsApp: ${data.phone}` : '',
  ].filter(Boolean);

  const text = encodeURIComponent(parts.join('\n'));
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
};

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    // Limita mensagem a 300 chars
    if (name === 'message' && value.length > MESSAGE_MAX) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpa erro ao digitar
    if (name === 'name' || name === 'phone') {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { name?: string; phone?: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData.phone.trim()) newErrors.phone = 'WhatsApp é obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    window.open(buildWhatsAppUrl(formData), '_blank');
  };

  return (
    <section id="contato" className="contact-section">
      <Container className="contact-container">
        <div className="section-header">
          <span className="section-badge">Contato</span>
          <h2 className="section-title">Fale com a Agricampanha</h2>
        </div>

        <p className="contact-subtitle">
          Quer se associar, apoiar nossos projetos ou tirar dúvidas?
          <br />
          Preencha o formulário e entraremos em contato.
        </p>

        <div className="contact-hours">
          <i className="bi bi-clock"></i>
          <span>Atendimento: <strong>Segunda a Sexta, 8h às 12h e 13h30 às 17h30</strong></span>
        </div>

        <form
          noValidate
          className="contact-form-inline"
          onSubmit={handleSubmit}
        >
          {/* Linha de campos */}
          <div className="contact-fields-row">
            <div className={`contact-field${errors.name ? ' contact-field--error' : ''}`}>
              <label htmlFor="contact-name">Nome completo *</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Seu nome"
              />
              {errors.name && <span className="contact-field-error-msg">{errors.name}</span>}
            </div>

            <div className="contact-field-divider" />

            <div className={`contact-field${errors.phone ? ' contact-field--error' : ''}`}>
              <label htmlFor="contact-phone">WhatsApp *</label>
              <input
                id="contact-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(53) 99999-9999"
              />
              {errors.phone && <span className="contact-field-error-msg">{errors.phone}</span>}
            </div>

            <div className="contact-field-divider" />

            <div className="contact-field contact-field--select">
              <label htmlFor="contact-subject">Assunto</label>
              <div className="contact-select-wrapper">
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={formData.subject ? 'has-value' : ''}
                >
                  <option value="" disabled hidden>Selecione</option>
                  <option value="Quero me associar">Quero me associar</option>
                  <option value="Parcerias">Parcerias</option>
                  <option value="Imprensa">Imprensa</option>
                  <option value="Outros assuntos">Outros assuntos</option>
                </select>
                <i className="bi bi-chevron-down contact-select-arrow"></i>
              </div>
            </div>
          </div>

          {/* Mensagem */}
          <div className="contact-message-row">
            <div className="contact-message-wrapper">
              <div className="contact-message-header">
                <label htmlFor="contact-message">Mensagem</label>
                <span className="contact-char-count">{formData.message.length}/{MESSAGE_MAX}</span>
              </div>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Como podemos ajudar? Deixe sua mensagem aqui..."
                rows={4}
                maxLength={MESSAGE_MAX}
              />
            </div>
          </div>

          <div className="contact-form-footer">
            <Button variant="primary" type="submit" className="contact-submit-btn">
              <i className="bi bi-whatsapp me-2"></i>
              Enviar mensagem
            </Button>
            <p className="contact-privacy-note">
              Seus dados são usados apenas para entrar em contato com você.
            </p>
          </div>
        </form>
      </Container>
    </section>
  );
};

export default ContactSection;
